import {
  AccountManager,
  actions,
  constants,
  deleteCookie,
  GLOBAL_ERROR_CODE,
  isEmptyCollection,
  NotificationManager,
  SessionStorageManager,
  SettingsManager,
  SiteConfigManager,
  StorageManager,
} from '@atlas/core';

import {
  ACCOUNT_STATUSES,
  currencySymbols,
  HIDE_FREEBET_FOR_CURRENT_SESSION,
  KYC_AUTO_RUN_KEY,
  SIGN_UP_POPUP_STORAGE_KEY,
} from 'common/constants';
import {
  getMultiPopupConfig,
  showMultiPopup,
} from 'common/Notifications/Popups/MultiPopup/MultiPopupManager';
import resolveSelfRegulationPopupVisibility from 'src/components/SelfRegulationProvider/resolveSelfRegulationPopupVisibility';
import resolveSpentLimitPopupVisibility from 'src/components/SelfRegulationProvider/resolveSpentLimitPopupVisibility';

const { AFFILIATE_PARTNER_COOKIE_NAME } = constants;

/*
  Bus Events Mapping:
    - Binding actions with bus events
    - Resource loading handler
    - Logging events

    - Binding history with bus too
*/
export default (Bus, history, customListeners = {}) => {
  /*
    Check if bus already got listeners
  */
  if (Bus.listenerCount('newListener') > 0) {
    return;
  }

  // Prioritized login handler
  Bus.prependListener(Bus.events.auth.login, () => {
    // Reset freebet reminder settings
    StorageManager.removeItem(HIDE_FREEBET_FOR_CURRENT_SESSION);
  });

  // On success place bet
  // Cleaning main betslip bet amount on successful place bet
  Bus.on(Bus.events.bet.placedSuccess, () => {
    Bus.emit('betslip:main', {
      payload: { amount: undefined },
      type: actions.BETSLIP_STATE_UPDATE,
    });
  });

  Bus.on(Bus.events.profile.preferencesUpdate, ({ data: preferences = {} }) => {
    const settingsUpdate = {};

    if (preferences.acceptOddsChanges) {
      settingsUpdate.acceptOddsChanges = preferences.acceptOddsChanges;
    }

    if (preferences.oddsFormat) {
      settingsUpdate.oddsFormat = preferences.oddsFormat;
    }

    if (!isEmptyCollection(settingsUpdate)) {
      SettingsManager.updateSettings(settingsUpdate);
    }
  });

  Bus.on(`betslip:main:${Bus.events.bet.snapshot}`, ({ betItems }) => {
    if (betItems.length < 1) {
      // Reset amount for main betslip
      Bus.emit('betslip:main', {
        payload: { amount: undefined },
        type: actions.BETSLIP_STATE_UPDATE,
      });

      // Disabling order bet
      if (SettingsManager.settings.orderBet) {
        SettingsManager.updateSettings({
          orderBet: false,
        });
      }
    }
  });

  Bus.on(`betslip:edit:${Bus.events.bet.snapshot}`, ({ addition }) => {
    if (addition) {
      Bus.emit('ui:scroll:betslip', {
        block: 'center',
        query: '.bet-order-outcome.new',
      });
    }
  });

  // Disabling order bet in settings after order success
  Bus.on(Bus.events.bet.orderSuccess, () => {
    SettingsManager.updateSettings({
      orderBet: false,
    });
  });

  // --------------------------
  // Auth
  // --------------------------
  let isJustRegistered = false;
  Bus.on(Bus.events.auth.formRegistrationSuccess, async credentials => {
    StorageManager.setItem(KYC_AUTO_RUN_KEY, '1');
    StorageManager.setItem('leavePopupShown', 'shown');
    deleteCookie(AFFILIATE_PARTNER_COOKIE_NAME);

    if (customListeners[Bus.events.auth.formRegistrationSuccess]) {
      return customListeners[Bus.events.auth.formRegistrationSuccess](credentials);
    }

    if (credentials.showPopup) {
      NotificationManager.display({
        data: credentials,
        type: 'oneClickSignUp',
      });
    }

    isJustRegistered = true;
  });

  Bus.on(Bus.events.auth.login, ({ client }) => {
    const sigapLimitedAt = client.additionalParameters?.SIGAP_ACCESS_LIMITED_AT;

    if (sigapLimitedAt) {
      NotificationManager.display({
        id: 'accountBlocked',
        type: 'accountBlocked',
      });
    }

    StorageManager.setItem('leavePopupShown', 'shown');
    if (!isJustRegistered) {
      NotificationManager.notify({
        message: 'welcome_back',
        status: 'success',
        type: 'notification',
      });
    }

    if (isJustRegistered) {
      const isShowBonusPopup = SiteConfigManager.getConfig(
        'config.config.registration.showBonusPopup',
      );

      if (isShowBonusPopup) {
        NotificationManager.display({
          data: {},
          type: 'registrationBonus',
        });
      } else {
        const signupMultiPopupConfig = getMultiPopupConfig('sign_up');
        if (signupMultiPopupConfig) {
          showMultiPopup('sign_up', SIGN_UP_POPUP_STORAGE_KEY);
        }
      }
    }

    Promise.allSettled([
      resolveSpentLimitPopupVisibility(client, true),
      resolveSelfRegulationPopupVisibility(client, true),
    ]).then(([shouldShowSpentLimit, shouldShowRealityCheck]) => {
      SessionStorageManager.setItem(
        'showSpentLimitPopup',
        shouldShowSpentLimit.value ? 'yes' : 'no',
      );
      SessionStorageManager.setItem(
        'showRealityCheckPopup',
        shouldShowRealityCheck.value ? 'yes' : 'no',
      );
    });
  });

  Bus.on(Bus.events.auth.logout, ({ withoutRedirect } = {}) => {
    isJustRegistered = false;

    if (AccountManager.account?.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED) {
      StorageManager.removeItem('leavePopupShown');
    }

    NotificationManager.removeNotification('depositSuccess');
    if (!withoutRedirect) {
      history.push(history.location.pathname);
    }
  });

  Bus.on(Bus.events.profile.realityCheck, () => {
    if (customListeners[Bus.events.profile.realityCheck]) {
      return customListeners[Bus.events.profile.realityCheck]();
    }

    NotificationManager.display({
      id: 'realityCheck',
      message: 'reality_check_popup_message',
      status: 'popup',
      title: 'reality_check_popup_title',
      type: 'realityCheck',
    });
  });

  // Loading client preferences on auth
  Bus.on(Bus.events.auth.resolved, () => {
    AccountManager.loadClientPreferences();
  }).on(Bus.events.auth.login, () => {
    AccountManager.loadClientPreferences();
  });

  Bus.on(Bus.events.bet.editBetSuccessResult, data => {
    NotificationManager.notify({
      data,
      message: 'edit_bet_success_message',
      status: 'success',
      type: 'notification',
    });
  });

  // Vip bets acceptance notification
  Bus.on(Bus.events.bet.vipPlacedSuccess, () => {
    NotificationManager.display({
      message: 'vip_place_bet_accepted',
      status: 'success',
      type: 'notification',
    });
  });

  Bus.on(Bus.events.bet.vipPlacedFail, () => {
    NotificationManager.display({
      message: 'vip_place_bet_rejected',
      status: 'error',
      type: 'notification',
    });
  });

  /*
    CASHOUT
  */
  Bus.on(Bus.events.history.cashoutSuccess, data => {
    const currencySymbol = currencySymbols[data.betMoney.currency] || data.betMoney.currency;

    NotificationManager.notify({
      data: {
        currency: currencySymbol,
        value: data.betMoney.betAmount,
      },
      message: 'cashout_success_message',
      status: 'success',
      type: 'notification',
    });
  });

  Bus.on(Bus.events.history.cashoutError, error => {
    const { code = GLOBAL_ERROR_CODE.INTERNAL_ERROR } = error;

    NotificationManager.notify({
      message: `cashout_error_${code}`,
      status: 'error',
      type: 'notification',
    });
  });

  Bus.on(Bus.events.history.partialCashoutSuccess, data => {
    const currencySymbol = currencySymbols[data.betMoney.currency] || data.betMoney.currency;

    NotificationManager.notify({
      data: {
        currency: currencySymbol,
        value: data.betMoney.betAmount,
      },
      id: 'partial_cashout',
      message: 'partial_cashout_success_message',
      status: 'success',
      type: 'notification',
    });
  });

  Bus.on(Bus.events.history.partialCashoutError, error => {
    const { code = GLOBAL_ERROR_CODE.INTERNAL_ERROR } = error;

    NotificationManager.notify({
      message: `partial_cashout_error_${code}`,
      status: 'error',
      type: 'notification',
    });
  });

  Bus.on(Bus.events.history.orderCashoutError, data => {
    if (data.cashOutType === 'PARTIAL_CASH_OUT') {
      NotificationManager.notify({
        data,
        message: 'order_partial_cashout_error_message',
        status: 'error',
        type: 'notification',
      });
    } else {
      NotificationManager.notify({
        data: { ...data, amount: data.violationInfo.estimatedCashOutAmount / 100 },
        message: 'order_cashout_error_notification',
        status: 'error',
        type: 'notification',
      });
    }
  });

  Bus.on(Bus.events.bonus.bonusInstanceStatusChanged, ({ data }) => {
    NotificationManager.toast({
      data: { bonus: data.bonusName },
      type: 'bonusActivationSuccess',
    });
  });

  Bus.on(Bus.events.config.resolved, () => {
    (() =>
      setTimeout(() => {
        const isBetBoosterActive = SiteConfigManager.getConfig('config.config.betBooster.isActive');

        if (isBetBoosterActive) {
          const script = document.createElement('script');
          script.type = 'text/javascript';
          script.src = 'https://betbooster-widget.lvision.io/v2/prod/bundle-es5.js';
          script.defer = true;

          document.head.appendChild(script);
        }
      }, 10000))();
  });

  Bus.once('preorder:resolved', () => {
    let initialBetItemsCount = 0;

    const betslipChangeHandler = ({ betItems }) => {
      if (!initialBetItemsCount) {
        initialBetItemsCount = betItems.length;
      } else {
        history.push(window.location.pathname);

        Bus.off(`betslip:main:${Bus.events.bet.snapshot}`, betslipChangeHandler);
      }
    };

    Bus.on(`betslip:main:${Bus.events.bet.snapshot}`, betslipChangeHandler);
  });
};
